import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Trash2, Pencil, Palette, Sun, Moon, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { ETAPAS, FUNCOES } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Slider } from "@/components/ui/slider";
import { useTheme } from "@/context/ThemeContext";
import Acessos from "@/pages/Acessos";
import Integracoes from "@/pages/Integracoes";
import Equipe from "@/pages/Equipe";

const novaTarefaTemplate = () => ({ titulo: "", etapa: "planejamento", offset_dias: 1, funcao: "Atendimento", somente_mes_captacao: false });
const emptyTpl = { nome: "", tipo: "mensal", tarefas: [novaTarefaTemplate()] };
const ACCENT_PRESETS = [
  { name: "Netzer", color: "#8CC63F" },
  { name: "Esmeralda", color: "#16A66A" },
  { name: "Azul", color: "#3B82F6" },
  { name: "Violeta", color: "#8B5CF6" },
  { name: "Laranja", color: "#F59E0B" },
  { name: "Rosa", color: "#EC4899" },
];

function Aparencia() {
  const { mode, accent, fontScale, setMode, setAccent, setFontScale, resetTheme } = useTheme();
  const [accentDraft, setAccentDraft] = useState(accent);
  const fontPercent = Math.round(fontScale * 100);

  useEffect(() => setAccentDraft(accent), [accent]);

  const applyAccentDraft = () => {
    if (/^#[0-9a-f]{6}$/i.test(accentDraft)) setAccent(accentDraft);
    else setAccentDraft(accent);
  };

  return (
    <div className="grid lg:grid-cols-[1fr_360px] gap-5" data-testid="aparencia-section">
      <div className="space-y-5">
        <section className="studio-card p-5 sm:p-6">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#8CC63F]/15 border border-[#8CC63F]/30 flex items-center justify-center text-[#8CC63F] shrink-0">
              <Palette size={19} />
            </div>
            <div>
              <h2 className="text-base font-semibold text-[#F4F4F0]">Cor principal</h2>
              <p className="text-sm text-[#71717A] mt-1">Escolha a cor usada em botões, destaques, links e indicadores ativos.</p>
            </div>
          </div>

          <div className="mt-5 flex flex-wrap gap-3">
            {ACCENT_PRESETS.map((preset) => (
              <button
                key={preset.color}
                type="button"
                data-testid={`accent-preset-${preset.name.toLowerCase()}`}
                onClick={() => setAccent(preset.color)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-all ${accent === preset.color ? "border-[#8CC63F] bg-[#8CC63F]/10 text-[#F4F4F0]" : "border-[#27272A] text-[#A1A1AA] hover:border-[#3F3F46]"}`}
              >
                <span className="w-4 h-4 rounded-full border border-white/20" style={{ backgroundColor: preset.color }} />
                {preset.name}
              </button>
            ))}
          </div>

          <div className="mt-4 flex items-center gap-3 max-w-sm">
            <input
              type="color"
              value={accent}
              onChange={(event) => setAccent(event.target.value)}
              data-testid="accent-custom-picker"
              aria-label="Escolher cor personalizada"
              className="h-10 w-12 rounded-lg border border-[#27272A] bg-transparent cursor-pointer p-1"
            />
            <Input
              value={accentDraft}
              onChange={(event) => setAccentDraft(event.target.value.toUpperCase())}
              onBlur={applyAccentDraft}
              onKeyDown={(event) => event.key === "Enter" && event.currentTarget.blur()}
              aria-label="Código hexadecimal da cor"
              className="bg-[#18181C] border-[#27272A] font-mono uppercase"
            />
          </div>
        </section>

        <section className="studio-card p-5 sm:p-6">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="text-base font-semibold text-[#F4F4F0]">Tamanho das letras</h2>
              <p className="text-sm text-[#71717A] mt-1">Ajusta proporcionalmente títulos, textos, menus e formulários.</p>
            </div>
            <span className="font-mono text-sm text-[#8CC63F]" data-testid="font-scale-value">{fontPercent}%</span>
          </div>
          <div className="mt-6 px-1">
            <Slider
              value={[fontPercent]}
              onValueChange={([value]) => setFontScale(value / 100)}
              min={100}
              max={125}
              step={5}
              aria-label="Tamanho global das letras"
              data-testid="font-scale-slider"
            />
            <div className="mt-3 flex justify-between font-mono text-[10px] uppercase tracking-wider text-[#71717A]">
              <span>Padrão</span><span>Confortável</span><span>Maior</span>
            </div>
          </div>
        </section>

        <section className="studio-card p-5 sm:p-6">
          <div>
            <h2 className="text-base font-semibold text-[#F4F4F0]">Modo de exibição</h2>
            <p className="text-sm text-[#71717A] mt-1">A troca é instantânea e todas as letras se adaptam para manter a leitura.</p>
          </div>
          <div className="mt-5 grid grid-cols-2 gap-3 max-w-md">
            <button type="button" data-testid="theme-dark-button" onClick={() => setMode("dark")} className={`flex items-center justify-center gap-2 rounded-xl border px-4 py-3 text-sm transition-colors ${mode === "dark" ? "border-[#8CC63F] bg-[#8CC63F]/10 text-[#8CC63F]" : "border-[#27272A] text-[#A1A1AA]"}`}>
              <Moon size={17} /> Escuro
            </button>
            <button type="button" data-testid="theme-light-button" onClick={() => setMode("light")} className={`flex items-center justify-center gap-2 rounded-xl border px-4 py-3 text-sm transition-colors ${mode === "light" ? "border-[#8CC63F] bg-[#8CC63F]/10 text-[#8CC63F]" : "border-[#27272A] text-[#A1A1AA]"}`}>
              <Sun size={17} /> Claro
            </button>
          </div>
        </section>
      </div>

      <aside className="studio-card p-5 sm:p-6 h-fit lg:sticky lg:top-8">
        <p className="caption-label">Prévia em tempo real</p>
        <div className="mt-4 rounded-xl border border-[#27272A] bg-[#18181C] p-5">
          <p className="caption-label">Netzer Studio OS</p>
          <h3 className="font-serif text-2xl text-[#F4F4F0] mt-2">Seu sistema, <span className="gold-italic">suas cores</span></h3>
          <p className="text-sm text-[#A1A1AA] mt-3">Texto e contraste acompanham o tema escolhido.</p>
          <button type="button" className="mt-5 w-full rounded-full bg-[#8CC63F] text-[#09090B] font-semibold text-sm py-2.5">Botão principal</button>
        </div>
        <Button type="button" variant="outline" onClick={resetTheme} data-testid="theme-reset-button" className="w-full mt-4 border-[#27272A] bg-transparent text-[#A1A1AA] hover:bg-[#18181C] hover:text-[#F4F4F0]">
          <RotateCcw size={15} className="mr-2" /> Restaurar padrão Netzer
        </Button>
        <p className="text-[11px] leading-relaxed text-[#71717A] mt-4">Suas preferências ficam salvas neste navegador e são aplicadas em todas as telas do sistema.</p>
      </aside>
    </div>
  );
}

function Templates() {
  const [templates, setTemplates] = useState([]);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyTpl);

  const load = () => api.get("/templates").then((r) => setTemplates(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const salvar = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, tarefas: form.tarefas.filter((t) => t.titulo) };
      if (editing) await api.patch(`/templates/${editing.id}`, payload);
      else await api.post("/templates", payload);
      toast.success("Template salvo");
      setDialog(false);
      setEditing(null);
      setForm(emptyTpl);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const excluir = async (t) => { await api.delete(`/templates/${t.id}`); toast.success("Template removido"); load(); };

  const setTarefa = (i, k, v) => { const ts = [...form.tarefas]; ts[i] = { ...ts[i], [k]: v }; setForm({ ...form, tarefas: ts }); };

  return (
    <div className="space-y-4" data-testid="templates-section">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[#A1A1AA]">Templates definem etapas, tarefas, prazos relativos (D+N) e funções padrão de cada tipo de ciclo.</p>
        <Button data-testid="novo-template-button" onClick={() => { setEditing(null); setForm(emptyTpl); setDialog(true); }} className="bg-[#8CC63F] text-[#09090B] hover:bg-[#A9DD6B] rounded-full font-semibold shrink-0 ml-4">
          <Plus size={15} className="mr-1" /> Novo template
        </Button>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {templates.map((t) => (
          <div key={t.id} data-testid={`template-card-${t.id}`} className="studio-card p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-[#E4E4E0]">{t.nome}</p>
                <p className="caption-label mt-1 normal-case tracking-normal">{t.tipo} · {t.tarefas.length} tarefas</p>
              </div>
              <div className="flex gap-1">
                <button data-testid={`template-editar-${t.id}`} onClick={() => { setEditing(t); setForm({ nome: t.nome, tipo: t.tipo, tarefas: t.tarefas.map((x) => ({ titulo: x.titulo, etapa: x.etapa, offset_dias: x.offset_dias ?? 1, funcao: x.funcao || "Atendimento", somente_mes_captacao: Boolean(x.somente_mes_captacao) })) }); setDialog(true); }} className="p-1.5 text-[#71717A] hover:text-[#8CC63F]"><Pencil size={14} /></button>
                <button data-testid={`template-excluir-${t.id}`} onClick={() => excluir(t)} className="p-1.5 text-[#71717A] hover:text-[#F43F5E]"><Trash2 size={14} /></button>
              </div>
            </div>
            <div className="mt-3 space-y-1">
              {t.tarefas.slice(0, 4).map((x, i) => (
                <p key={i} className="text-[11px] text-[#71717A] font-mono">D+{x.offset_dias ?? "—"} · {x.titulo}{x.somente_mes_captacao ? " · mês de captação" : ""}</p>
              ))}
              {t.tarefas.length > 4 && <p className="text-[11px] text-[#3F3F46] font-mono">+{t.tarefas.length - 4} tarefas</p>}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="bg-[#141418] border-[#27272A] text-[#F4F4F0] max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{editing ? "Editar" : "Novo"} <span className="gold-italic">template</span></DialogTitle></DialogHeader>
          <form onSubmit={salvar} className="space-y-4 mt-2">
            <div className="grid gap-4 sm:grid-cols-2">
              <div><Label className="caption-label">Nome</Label>
                <Input data-testid="template-nome-input" required value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" /></div>
              <div><Label className="caption-label">Tipo de ciclo</Label>
                <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                  <SelectTrigger data-testid="template-tipo-select" className="mt-1.5 bg-[#18181C] border-[#27272A]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#18181C] border-[#27272A] text-[#E4E4E0]">
                    <SelectItem value="mensal">Mensal</SelectItem>
                    <SelectItem value="campanha">Campanha</SelectItem>
                    <SelectItem value="pontual">Pontual</SelectItem>
                  </SelectContent>
                </Select></div>
            </div>
            <p className="caption-label">Tarefas do template</p>
            {form.tarefas.map((t, i) => (
              <div key={i} className="rounded-lg border border-[#27272A] p-2.5 space-y-2">
                <div className="grid gap-2 sm:grid-cols-[1fr_130px_76px_130px]">
                  <Input data-testid={`tpl-tarefa-titulo-${i}`} placeholder="Título da tarefa" value={t.titulo} onChange={(e) => setTarefa(i, "titulo", e.target.value)} className="bg-[#18181C] border-[#27272A] h-9 text-xs" />
                  <Select value={t.etapa} onValueChange={(v) => setTarefa(i, "etapa", v)}>
                    <SelectTrigger className="bg-[#18181C] border-[#27272A] h-9 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#18181C] border-[#27272A] text-[#E4E4E0]">
                      {ETAPAS.filter((e) => e.key !== "finalizado").map((x) => <SelectItem key={x.key} value={x.key}>{x.nome}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input data-testid={`tpl-tarefa-offset-${i}`} type="number" min="0" placeholder="D+" value={t.offset_dias} onChange={(e) => setTarefa(i, "offset_dias", parseInt(e.target.value) || 0)} className="bg-[#18181C] border-[#27272A] h-9 text-xs" />
                  <Select value={t.funcao} onValueChange={(v) => setTarefa(i, "funcao", v)}>
                    <SelectTrigger className="bg-[#18181C] border-[#27272A] h-9 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#18181C] border-[#27272A] text-[#E4E4E0]">
                      {FUNCOES.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <button type="button" data-testid={`tpl-tarefa-captacao-${i}`} aria-pressed={Boolean(t.somente_mes_captacao)} onClick={() => setTarefa(i, "somente_mes_captacao", !t.somente_mes_captacao)}
                  className={`rounded-full border px-3 py-1 text-[10px] font-mono uppercase tracking-wide transition-colors ${t.somente_mes_captacao ? "border-[#8CC63F]/50 bg-[#8CC63F]/10 text-[#8CC63F]" : "border-[#3F3F46] text-[#71717A] hover:text-[#A1A1AA]"}`}>
                  {t.somente_mes_captacao ? "✓ Somente no mês de captação" : "Todo mês"}
                </button>
              </div>
            ))}
            <button type="button" data-testid="tpl-add-tarefa" onClick={() => setForm({ ...form, tarefas: [...form.tarefas, novaTarefaTemplate()] })} className="text-xs font-mono text-[#8CC63F] hover:text-[#A9DD6B]">+ adicionar tarefa</button>
            <Button data-testid="template-submit-button" type="submit" className="w-full bg-[#8CC63F] text-[#09090B] hover:bg-[#A9DD6B] rounded-full font-semibold">Salvar template</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Configuracoes() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabs = ["aparencia", "templates", "equipe", "acessos", "integracoes"];
  const activeTab = searchParams.get("google") === "conectado"
    ? "integracoes"
    : tabs.includes(searchParams.get("tab")) ? searchParams.get("tab") : "aparencia";
  const trocarTab = (tab) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", tab);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="space-y-6" data-testid="configuracoes-page">
      <div>
        <p className="caption-label mb-2">05 // Configurações</p>
        <h1 className="font-serif text-3xl sm:text-4xl text-[#F4F4F0] tracking-tight">Configu<span className="gold-italic">rações</span></h1>
      </div>
      <Tabs value={activeTab} onValueChange={trocarTab}>
        <TabsList className="h-auto w-full justify-start overflow-x-auto border border-[#27272A] bg-[#141418]">
          <TabsTrigger value="aparencia" data-testid="tab-aparencia">Aparência</TabsTrigger>
          <TabsTrigger value="templates" data-testid="tab-templates">Templates operacionais</TabsTrigger>
          <TabsTrigger value="equipe" data-testid="tab-equipe">Equipe</TabsTrigger>
          <TabsTrigger value="acessos" data-testid="tab-acessos">Acessos</TabsTrigger>
          <TabsTrigger value="integracoes" data-testid="tab-integracoes">Integrações</TabsTrigger>
        </TabsList>
        <TabsContent value="aparencia" className="mt-6"><Aparencia /></TabsContent>
        <TabsContent value="templates" className="mt-6"><Templates /></TabsContent>
        <TabsContent value="equipe" className="mt-6"><Equipe embedded /></TabsContent>
        <TabsContent value="acessos" className="mt-6"><Acessos embedded /></TabsContent>
        <TabsContent value="integracoes" className="mt-6"><Integracoes embedded /></TabsContent>
      </Tabs>
    </div>
  );
}
